import React, { useEffect, useState } from "react";
import { NavLink, Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { TrendingUp, Bell } from "lucide-react";
import { MarketingProvider, fmtDT } from "@/components/marketing/shared";
import Overview from "@/components/marketing/Overview";
import MyDashboard from "@/components/marketing/MyDashboard";
import Leads from "@/components/marketing/Leads";
import Pipeline from "@/components/marketing/Pipeline";
import Executives from "@/components/marketing/Executives";
import Rules from "@/components/marketing/Rules";
import Products from "@/components/marketing/Products";
import Sales from "@/components/marketing/Sales";
import CrmTasks from "@/components/marketing/CrmTasks";
import Campaigns from "@/components/marketing/Campaigns";
import Materials from "@/components/marketing/Materials";
import Incentives from "@/components/marketing/Incentives";
import Automation from "@/components/marketing/Automation";
import Reports from "@/components/marketing/Reports";
import AiAssistant from "@/components/marketing/AiAssistant";

function Bellbox() {
  const [d, setD] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const load = () => api.get("/marketing/notifications").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 45000); return () => clearInterval(t); }, []);
  return (
    <div className="relative">
      <button data-testid="mkt-bell" onClick={() => { setOpen((o) => !o); if (d.unread) api.post("/marketing/notifications/read").then(load); }} className="relative p-2 border border-zinc-200 bg-white hover:bg-zinc-100"><Bell size={15} />{d.unread ? <span data-testid="mkt-bell-count" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-rose-600 text-white text-[10px] font-bold grid place-items-center">{d.unread}</span> : null}</button>
      {open ? <div data-testid="mkt-bell-panel" className="absolute right-0 mt-1 w-80 max-h-96 overflow-y-auto bg-white border border-zinc-200 shadow-xl z-40">{d.items.length ? d.items.map((n) => <div key={n.id} className={`px-3 py-2 border-b border-zinc-100 text-xs ${n.read ? "" : "bg-zinc-50"}`}><div className="font-semibold">{n.title}</div><div className="text-zinc-600">{n.body}</div><div className="text-[10px] text-zinc-400 mt-0.5">{fmtDT(n.created_at)}</div></div>) : <div className="p-4 text-xs text-zinc-500">No notifications</div>}</div> : null}
    </div>
  );
}

export default function Marketing() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const tabs = isAdmin
    ? [["", "Overview"], ["leads", "Leads & CRM"], ["pipeline", "Pipeline"], ["tasks", "Tasks & Follow-ups"], ["sales", "Sales & Approvals"], ["products", "Products & Packages"], ["campaigns", "Campaigns"], ["materials", "Materials"], ["incentives", "Targets & Incentives"], ["automation", "Automation"], ["reports", "Reports & Audit"], ["ai", "AI Assistant"], ["executives", "Executives"], ["rules", "Assignment & Settings"]]
    : [["", "My Dashboard"], ["leads", "My Leads"], ["pipeline", "Pipeline"], ["tasks", "My Tasks"], ["sales", "My Sales"], ["products", "Product Catalogue"], ["campaigns", "Campaigns"], ["materials", "Materials"], ["incentives", "My Incentives"], ["reports", "My Reports"], ["ai", "AI Assistant"]];
  return (
    <MarketingProvider>
      <div className="space-y-6" data-testid="marketing-page">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-semibold">{isAdmin ? "Admin control panel" : "Executive workspace"}</div>
            <h1 className="font-heading font-black tracking-tight text-3xl sm:text-4xl mt-2 flex items-center gap-3"><TrendingUp size={28} strokeWidth={1.75} /> Marketing &amp; Sales</h1>
          </div>
          <Bellbox />
        </header>
        <nav className="flex gap-1 border-b border-zinc-200 overflow-x-auto" data-testid="mkt-tabs">
          {tabs.map(([p, l]) => <NavLink key={p} to={`/marketing/${p}`} end={p === ""} data-testid={`mkt-tab-${p || "home"}`} className={({ isActive }) => `px-3 py-2 text-xs font-semibold whitespace-nowrap border-b-2 -mb-px ${isActive ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}>{l}</NavLink>)}
        </nav>
        <Routes>
          <Route index element={isAdmin ? <Overview /> : <MyDashboard />} />
          <Route path="leads" element={<Leads />} />
          <Route path="pipeline" element={<Pipeline />} />
          <Route path="tasks" element={<CrmTasks />} />
          <Route path="sales" element={<Sales />} />
          <Route path="products" element={<Products />} />
          <Route path="campaigns" element={<Campaigns />} />
          <Route path="materials" element={<Materials />} />
          <Route path="incentives" element={<Incentives />} />
          <Route path="reports" element={<Reports />} />
          <Route path="ai" element={<AiAssistant />} />
          <Route path="automation" element={isAdmin ? <Automation /> : <Navigate to="/marketing" replace />} />
          <Route path="executives" element={isAdmin ? <Executives /> : <Navigate to="/marketing" replace />} />
          <Route path="rules" element={isAdmin ? <Rules /> : <Navigate to="/marketing" replace />} />
        </Routes>
      </div>
    </MarketingProvider>
  );
}
